import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdExpandMore, MdExpandLess, MdAutoFixHigh, MdSend, MdClear } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../redux/store';
import type { ProductConfig, TabId } from './customizerConfig';
import { clearCustomizations, resetStream } from '../../redux/slices/customizationSlice';
import { THEME } from './theme';
import CustomizationTabs from './CustomizationTabs';
import SelectionsSummary from './shared/SelectionsSummary';
import FabricSection from './sections/FabricSection';
import FinishSection from './sections/FinishSection';
import PillowSection from './sections/PillowSection';
import NailheadSection from './sections/NailheadSection';

interface TearSheetPanelProps {
  dimensions: string;
  dimensionsEdited: boolean;
  isGenerating: boolean;
  error: string | null;
  generatedImageUrl: string | null;
  onDimensionsChange: (value: string) => void;
  onGenerate: () => void;
}

interface CustomizationPanelProps {
  product: ProductConfig;
  onGenerate: () => void;
  streamStatus: string;
  tearSheet?: TearSheetPanelProps;
}

const CustomizationPanel: React.FC<CustomizationPanelProps> = ({ product, onGenerate, streamStatus, tearSheet }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<TabId>(product.tabs[0]);
  const [tearSheetOpen, setTearSheetOpen] = useState(true);

  const customization = useSelector((s: RootState) => s.customization);

  // Check if any customization is selected
  const fabricOnlyPillow = product.pillowConfig && !product.pillowConfig.showImageAndCount && customization.pillowFabricUrl;
  const hasAnySelection = !!(
    customization.frameFabricUrl ||
    customization.backCushionFabricUrl ||
    customization.seatFabricUrl ||
    customization.armrestFabricUrl ||
    customization.finishUrl ||
    (customization.pillowFile && customization.pillowFabricUrl) ||
    fabricOnlyPillow ||
    customization.nailheadFile
  );

  const isStreaming = streamStatus === 'streaming';
  const canGenerate = hasAnySelection && !isStreaming;

  const canGenerateTearSheet = tearSheet && tearSheet.dimensionsEdited && tearSheet.dimensions.trim().length > 0 && !tearSheet.isGenerating;

  return (
    <div className="h-full flex flex-col">
      {/* Header with Generate button */}
      <div className="px-7 pt-7 pb-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${THEME.border}` }}>
        <h1 className="font-heading text-2xl font-bold text-white mt-1.5 tracking-tight leading-tight">Customize</h1>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {hasAnySelection && !isStreaming && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => { dispatch(clearCustomizations()); dispatch(resetStream()); }}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-semibold cursor-pointer transition-all duration-200"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  color: 'rgba(255,255,255,0.5)',
                  border: `1px solid ${THEME.border}`,
                }}
              >
                <MdClear size={13} />
                <span>Clear All</span>
              </motion.button>
            )}
          </AnimatePresence>
        <motion.button
          whileHover={{ scale: canGenerate ? 1.05 : 1 }}
          whileTap={{ scale: canGenerate ? 0.95 : 1 }}
          onClick={onGenerate}
          disabled={!canGenerate}
          className="flex items-center gap-2 px-5 py-2 rounded-full text-[12px] font-bold cursor-pointer transition-all duration-200 disabled:cursor-not-allowed"
          style={{
            background: canGenerate
              ? `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryDark})`
              : 'rgba(255,255,255,0.04)',
            color: canGenerate ? '#fff' : 'rgba(255,255,255,0.2)',
            boxShadow: canGenerate ? THEME.primaryGlow : 'none',
            border: canGenerate ? 'none' : `1px solid ${THEME.border}`,
            opacity: canGenerate ? 1 : 0.5,
          }}
        >
          {isStreaming ? (
            <>
              <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <span>Generate</span>
              <MdSend size={13} />
            </>
          )}
        </motion.button>
        </div>
      </div>

      {/* Selections summary strip */}
      <AnimatePresence>
        <div className="px-7 pt-4 pb-0">
          <SelectionsSummary
            fabricZones={product.fabricZones}
            onNavigateTab={(tab) => setActiveTab(tab)}
          />
        </div>
      </AnimatePresence>

      <div className="px-7 pt-4 pb-3">
        <CustomizationTabs tabs={product.tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <div className="px-7 flex-1 min-h-0 overflow-y-auto">
        <AnimatePresence mode="wait">
          {activeTab === 'fabric' && <FabricSection key="fabric" zones={product.fabricZones} />}
          {activeTab === 'finish' && <FinishSection key="finish" finishSwatchKey={product.finishSwatchKey} />}
          {activeTab === 'pillow' && <PillowSection key="pillow" pillowConfig={product.pillowConfig} />}
          {activeTab === 'stud' && <NailheadSection key="stud" studConfig={product.studConfig} />}
        </AnimatePresence>
      </div>

      {tearSheet && (
        <div style={{ borderTop: `1px solid ${THEME.border}` }}>
          <button onClick={() => setTearSheetOpen(!tearSheetOpen)} className="w-full px-7 py-4 flex items-center justify-between cursor-pointer group">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/40 group-hover:text-white/60 transition-colors">Tear Sheet</span>
              {tearSheet.generatedImageUrl && (
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              )}
            </div>
            <motion.div animate={{ rotate: tearSheetOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              {tearSheetOpen ? <MdExpandLess size={18} className="text-white/30" /> : <MdExpandMore size={18} className="text-white/30" />}
            </motion.div>
          </button>
          <AnimatePresence>
            {tearSheetOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="overflow-hidden"
              >
                <div className="px-7 pb-5">
                  <textarea
                    value={tearSheet.dimensions}
                    onChange={(e) => tearSheet.onDimensionsChange(e.target.value)}
                    placeholder='29" H x 32" W x 35" D'
                    rows={3}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs text-white placeholder-gray-600 resize-none focus:outline-none focus:border-[#EE4423]/40 focus:ring-1 focus:ring-[#EE4423]/20 transition-all font-mono leading-relaxed"
                  />

                  <AnimatePresence>
                    {tearSheet.error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2"
                      >
                        <div className="px-2.5 py-2 rounded-lg bg-red-500/10 border border-red-500/15">
                          <p className="text-[10px] text-red-400">{tearSheet.error}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {(tearSheet.dimensionsEdited || tearSheet.isGenerating) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-3"
                      >
                        <motion.button
                          whileHover={{ scale: canGenerateTearSheet ? 1.01 : 1 }}
                          whileTap={{ scale: canGenerateTearSheet ? 0.98 : 1 }}
                          onClick={tearSheet.onGenerate}
                          disabled={!canGenerateTearSheet}
                          className={`w-full py-2 rounded-lg font-semibold text-[11px] flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer ${
                            canGenerateTearSheet
                              ? 'bg-gradient-to-r from-[#EE4423] to-[#d63d20] text-white shadow-md shadow-[#EE4423]/15 hover:shadow-[#EE4423]/25'
                              : 'bg-white/[0.04] text-gray-600 cursor-not-allowed border border-white/[0.06]'
                          }`}
                        >
                          {tearSheet.isGenerating ? (
                            <>
                              <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <MdAutoFixHigh size={14} />
                              {tearSheet.generatedImageUrl ? 'Regenerate Tear Sheet' : 'Generate Tear Sheet'}
                            </>
                          )}
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default CustomizationPanel;
